import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import {
  Plus, Trash2, Edit, Eye, EyeOff, ExternalLink, Image as ImageIcon,
  Upload, X, FileText, Search, Calendar, BarChart3, Globe, Sparkles
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const slugify = (s) =>
  (s || '')
    .toLowerCase()
    .replace(/ş/g, 's').replace(/ç/g, 'c').replace(/ğ/g, 'g')
    .replace(/ı/g, 'i').replace(/ö/g, 'o').replace(/ü/g, 'u')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 80);

const EMPTY_POST = {
  slug: '',
  title: '',
  excerpt: '',
  content: '',
  cover_image: '',
  keywords: [],
  published: true,
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
};

const ImageUploader = ({ value, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const [mode, setMode] = useState(value ? 'preview' : 'upload');
  const [urlInput, setUrlInput] = useState('');
  const fileRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) return toast.error('Lütfen bir görsel dosyası seçin');
    if (file.size > 10 * 1024 * 1024) return toast.error('Dosya boyutu 10MB altında olmalı');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/upload/image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      let url = res.data.url;
      if (url.startsWith('/api/')) url = `${process.env.REACT_APP_BACKEND_URL}${url}`;
      onChange(url);
      setMode('preview');
      toast.success('Kapak fotoğrafı yüklendi');
    } catch (e) {
      toast.error('Yüklenemedi');
    } finally {
      setUploading(false);
    }
  };

  if (mode === 'preview' && value) {
    return (
      <div className="space-y-2">
        <Label>Kapak Görseli</Label>
        <div className="relative rounded-xl overflow-hidden border border-slate-200 bg-slate-50">
          <img src={value} alt="Kapak" className="w-full h-48 object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
          <button
            type="button"
            onClick={() => { onChange(''); setMode('upload'); }}
            className="absolute top-2 right-2 bg-white/90 backdrop-blur p-1.5 rounded-full hover:bg-white shadow"
            data-testid="blog-cover-clear"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Label>Kapak Görseli</Label>
      <div className="flex gap-2">
        <Button
          type="button"
          variant={mode === 'upload' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setMode('upload')}
          data-testid="blog-cover-upload-mode"
        >
          <Upload className="w-3.5 h-3.5 mr-1" /> Yükle
        </Button>
        <Button
          type="button"
          variant={mode === 'url' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setMode('url')}
          data-testid="blog-cover-url-mode"
        >
          <Globe className="w-3.5 h-3.5 mr-1" /> URL
        </Button>
      </div>
      {mode === 'upload' ? (
        <div
          onClick={() => fileRef.current?.click()}
          className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50/30 transition-all"
        >
          <ImageIcon className="w-10 h-10 mx-auto text-slate-400 mb-2" />
          <p className="text-sm text-slate-600">{uploading ? 'Yükleniyor...' : 'Tıkla ya da sürükle (max 10MB)'}</p>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
            data-testid="blog-cover-file-input"
          />
        </div>
      ) : (
        <div className="flex gap-2">
          <Input
            placeholder="https://..."
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            data-testid="blog-cover-url-input"
          />
          <Button
            type="button"
            onClick={() => {
              if (!urlInput.trim()) return;
              onChange(urlInput.trim());
              setMode('preview');
            }}
            data-testid="blog-cover-url-apply"
          >
            Uygula
          </Button>
        </div>
      )}
    </div>
  );
};

const BlogTab = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all | published | draft
  const [showForm, setShowForm] = useState(false);
  const [editingSlug, setEditingSlug] = useState(null);
  const [form, setForm] = useState(EMPTY_POST);
  const [keywordInput, setKeywordInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [previewPost, setPreviewPost] = useState(null);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/blog/posts?published_only=false&limit=200`);
      setPosts(res.data || []);
    } catch (e) {
      toast.error('Yazılar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPosts(); }, []);

  const stats = useMemo(() => {
    const totalViews = posts.reduce((sum, p) => sum + (p.views || 0), 0);
    return {
      total: posts.length,
      published: posts.filter(p => p.published).length,
      drafts: posts.filter(p => !p.published).length,
      totalViews,
    };
  }, [posts]);

  const filteredPosts = useMemo(() => {
    return posts.filter(p => {
      if (statusFilter === 'published' && !p.published) return false;
      if (statusFilter === 'draft' && p.published) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        return p.title?.toLowerCase().includes(q) || p.slug?.toLowerCase().includes(q) || (p.keywords || []).some(k => k.toLowerCase().includes(q));
      }
      return true;
    });
  }, [posts, searchQuery, statusFilter]);

  const openCreate = () => {
    setForm(EMPTY_POST);
    setEditingSlug(null);
    setKeywordInput('');
    setShowForm(true);
  };

  const openEdit = (post) => {
    setForm({
      slug: post.slug,
      title: post.title,
      excerpt: post.excerpt,
      content: post.content || '',
      cover_image: post.cover_image || '',
      keywords: post.keywords || [],
      published: post.published,
    });
    setEditingSlug(post.slug);
    setKeywordInput('');
    setShowForm(true);
  };

  const loadFullPostForEdit = async (slug) => {
    // List endpoint omits content. Fetch single (force-show even if draft).
    try {
      // /blog/posts/{slug} requires published, so fetch with all=true via list
      const res = await axios.get(`${API}/blog/posts?published_only=false&limit=200`);
      const item = (res.data || []).find(p => p.slug === slug);
      if (!item) return;
      // Get full content from public endpoint if published, else we need a separate fetch.
      // Simplest: temporarily publish or fetch directly
      try {
        const full = await axios.get(`${API}/blog/posts/${slug}`);
        openEdit(full.data);
      } catch {
        // Fallback: edit with empty content (rare for drafts not yet saved)
        openEdit(item);
      }
    } catch (e) {
      toast.error('Yazı yüklenemedi');
    }
  };

  const addKeyword = () => {
    const k = keywordInput.trim();
    if (!k) return;
    if (form.keywords.includes(k)) return setKeywordInput('');
    setForm({ ...form, keywords: [...form.keywords, k] });
    setKeywordInput('');
  };

  const removeKeyword = (k) => setForm({ ...form, keywords: form.keywords.filter(x => x !== k) });

  const handleSave = async () => {
    if (!form.title.trim()) return toast.error('Başlık zorunlu');
    if (!form.excerpt.trim()) return toast.error('Özet zorunlu');
    if (!form.content.trim()) return toast.error('İçerik zorunlu');
    const slug = (form.slug || slugify(form.title)).trim();
    if (!slug) return toast.error('Slug oluşturulamadı');

    setSaving(true);
    try {
      if (editingSlug) {
        await axios.put(`${API}/blog/posts/${editingSlug}`, {
          title: form.title,
          excerpt: form.excerpt,
          content: form.content,
          cover_image: form.cover_image || null,
          keywords: form.keywords,
          published: form.published,
        });
        toast.success('Yazı güncellendi');
      } else {
        await axios.post(`${API}/blog/posts`, {
          slug,
          title: form.title,
          excerpt: form.excerpt,
          content: form.content,
          cover_image: form.cover_image || null,
          keywords: form.keywords,
          published: form.published,
        });
        toast.success('Yazı oluşturuldu');
      }
      setShowForm(false);
      fetchPosts();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Kaydedilemedi';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (slug) => {
    if (!window.confirm('Bu yazıyı silmek istediğinize emin misiniz?')) return;
    try {
      await axios.delete(`${API}/blog/posts/${slug}`);
      toast.success('Yazı silindi');
      fetchPosts();
    } catch (e) {
      toast.error('Silinemedi');
    }
  };

  const togglePublish = async (post) => {
    try {
      await axios.put(`${API}/blog/posts/${post.slug}`, { published: !post.published });
      toast.success(post.published ? 'Taslağa alındı' : 'Yayına alındı');
      fetchPosts();
    } catch (e) {
      toast.error('İşlem başarısız');
    }
  };

  return (
    <div className="space-y-6" data-testid="blog-tab">
      {/* Hero / Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0052A5] text-white p-6 sm:p-8 shadow-xl">
        <div className="absolute -top-12 -right-12 w-64 h-64 bg-white/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-16 -left-16 w-72 h-72 bg-blue-400/10 rounded-full blur-3xl" />
        <div className="relative flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-white/60 mb-2 inline-flex items-center gap-2">
              <Sparkles className="w-3 h-3" /> İçerik Yönetimi
            </p>
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight" style={{ fontFamily: 'Outfit, system-ui, sans-serif' }}>
              Blog Yazıları
            </h1>
            <p className="text-white/70 text-sm mt-2 max-w-xl">
              SEO uyumlu blog içeriklerini yönetin. Yazıları yayınlayıp görünürlüğünüzü artırın, taslakları daha sonra düzenleyin.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => window.open('/blog', '_blank')}
              variant="outline"
              className="bg-white/10 border-white/20 text-white hover:bg-white/20 backdrop-blur"
              data-testid="blog-view-public"
            >
              <ExternalLink className="w-4 h-4 mr-2" /> Siteyi Görüntüle
            </Button>
            <Button
              onClick={openCreate}
              className="bg-[#FF3B30] hover:bg-[#E63329] text-white shadow-lg shadow-red-500/30"
              data-testid="blog-new-post-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Yeni Yazı
            </Button>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-white border-l-4 border-blue-600 rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wide font-semibold mb-1">
            <FileText className="w-3.5 h-3.5" /> Toplam Yazı
          </div>
          <div className="text-2xl font-black text-slate-900">{stats.total}</div>
        </div>
        <div className="bg-white border-l-4 border-emerald-500 rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wide font-semibold mb-1">
            <Eye className="w-3.5 h-3.5" /> Yayında
          </div>
          <div className="text-2xl font-black text-emerald-700">{stats.published}</div>
        </div>
        <div className="bg-white border-l-4 border-amber-500 rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wide font-semibold mb-1">
            <EyeOff className="w-3.5 h-3.5" /> Taslak
          </div>
          <div className="text-2xl font-black text-amber-700">{stats.drafts}</div>
        </div>
        <div className="bg-white border-l-4 border-purple-600 rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wide font-semibold mb-1">
            <BarChart3 className="w-3.5 h-3.5" /> Toplam Görüntülenme
          </div>
          <div className="text-2xl font-black text-purple-700">{stats.totalViews.toLocaleString('tr-TR')}</div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 flex flex-col md:flex-row gap-3 items-stretch md:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Başlık, slug veya etiket ara..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
            data-testid="blog-search"
          />
        </div>
        <div className="flex gap-2">
          {[
            { v: 'all', l: 'Tümü' },
            { v: 'published', l: 'Yayında' },
            { v: 'draft', l: 'Taslak' },
          ].map(opt => (
            <Button
              key={opt.v}
              variant={statusFilter === opt.v ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter(opt.v)}
              data-testid={`blog-filter-${opt.v}`}
            >
              {opt.l}
            </Button>
          ))}
        </div>
      </div>

      {/* Posts Grid */}
      {loading ? (
        <div className="text-center py-20 text-slate-400">Yükleniyor...</div>
      ) : filteredPosts.length === 0 ? (
        <div className="bg-white border-2 border-dashed border-slate-300 rounded-xl p-12 text-center">
          <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 mb-4">{posts.length === 0 ? 'Henüz yazı yok' : 'Filtreye uygun yazı bulunamadı'}</p>
          {posts.length === 0 && (
            <Button onClick={openCreate} data-testid="blog-empty-create">
              <Plus className="w-4 h-4 mr-2" /> İlk Yazıyı Oluştur
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredPosts.map(post => (
            <div
              key={post.slug}
              className="bg-white rounded-xl overflow-hidden border border-slate-200 hover:shadow-lg hover:border-blue-300 transition-all flex flex-col"
              data-testid={`blog-card-${post.slug}`}
            >
              {post.cover_image ? (
                <div className="relative aspect-[16/9] bg-slate-100 overflow-hidden">
                  <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" />
                  <Badge className={`absolute top-3 right-3 ${post.published ? 'bg-emerald-600 hover:bg-emerald-600' : 'bg-amber-500 hover:bg-amber-500'} text-white border-0`}>
                    {post.published ? 'Yayında' : 'Taslak'}
                  </Badge>
                </div>
              ) : (
                <div className="aspect-[16/9] bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center relative">
                  <ImageIcon className="w-10 h-10 text-slate-400" />
                  <Badge className={`absolute top-3 right-3 ${post.published ? 'bg-emerald-600' : 'bg-amber-500'} text-white border-0`}>
                    {post.published ? 'Yayında' : 'Taslak'}
                  </Badge>
                </div>
              )}
              <div className="p-4 flex-1 flex flex-col">
                <div className="flex items-center gap-3 text-[11px] text-slate-500 mb-2">
                  <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(post.created_at)}</span>
                  <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3" />{post.views || 0}</span>
                </div>
                <h3 className="font-bold text-slate-900 leading-snug mb-1 line-clamp-2" style={{ fontFamily: 'Outfit, system-ui, sans-serif' }}>
                  {post.title}
                </h3>
                <p className="text-xs text-slate-500 font-mono mb-2 truncate">/{post.slug}</p>
                <p className="text-sm text-slate-600 line-clamp-2 mb-3">{post.excerpt}</p>
                {post.keywords?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {post.keywords.slice(0, 3).map((k, i) => (
                      <span key={i} className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded">{k}</span>
                    ))}
                    {post.keywords.length > 3 && (
                      <span className="text-[10px] text-slate-400 px-1">+{post.keywords.length - 3}</span>
                    )}
                  </div>
                )}
                <div className="mt-auto flex items-center gap-1 pt-2 border-t border-slate-100">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => loadFullPostForEdit(post.slug)}
                    className="flex-1"
                    data-testid={`blog-edit-${post.slug}`}
                  >
                    <Edit className="w-3.5 h-3.5 mr-1" /> Düzenle
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => togglePublish(post)}
                    className="text-slate-600"
                    title={post.published ? 'Yayından kaldır' : 'Yayına al'}
                    data-testid={`blog-toggle-${post.slug}`}
                  >
                    {post.published ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </Button>
                  {post.published && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => window.open(`/blog/${post.slug}`, '_blank')}
                      className="text-blue-600"
                      title="Yayında görüntüle"
                      data-testid={`blog-view-${post.slug}`}
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDelete(post.slug)}
                    className="text-red-600 hover:bg-red-50 hover:text-red-700"
                    title="Sil"
                    data-testid={`blog-delete-${post.slug}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black" style={{ fontFamily: 'Outfit, system-ui, sans-serif' }}>
              {editingSlug ? 'Yazıyı Düzenle' : 'Yeni Blog Yazısı'}
            </DialogTitle>
            <DialogDescription>
              SEO için başlık + özet + anahtar kelimeleri özenle doldurun. İçerik markdown formatını destekler.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* Title */}
            <div className="space-y-2">
              <Label>Başlık *</Label>
              <Input
                value={form.title}
                onChange={(e) => {
                  const title = e.target.value;
                  setForm(prev => ({
                    ...prev,
                    title,
                    slug: editingSlug ? prev.slug : slugify(title),
                  }));
                }}
                placeholder="Şirket içi spor şenlikleri nasıl organize edilir?"
                data-testid="blog-form-title"
              />
            </div>

            {/* Slug */}
            <div className="space-y-2">
              <Label>Slug (URL) {editingSlug && <span className="text-xs text-slate-500 font-normal">(değiştirilemez)</span>}</Label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500 whitespace-nowrap">/blog/</span>
                <Input
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })}
                  placeholder="ornek-yazi-basligi"
                  disabled={!!editingSlug}
                  className="font-mono text-sm"
                  data-testid="blog-form-slug"
                />
              </div>
            </div>

            {/* Cover Image */}
            <ImageUploader
              value={form.cover_image}
              onChange={(v) => setForm({ ...form, cover_image: v })}
            />

            {/* Excerpt */}
            <div className="space-y-2">
              <Label>Özet * <span className="text-xs text-slate-500 font-normal">(meta description — 150-160 karakter ideal)</span></Label>
              <Textarea
                value={form.excerpt}
                onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                placeholder="Yazının özetini buraya yazın..."
                rows={3}
                data-testid="blog-form-excerpt"
              />
              <p className="text-xs text-slate-400">{form.excerpt.length} karakter</p>
            </div>

            {/* Content */}
            <div className="space-y-2">
              <Label>İçerik * <span className="text-xs text-slate-500 font-normal">(Markdown desteklenir: ## başlık, **kalın**, - liste)</span></Label>
              <Textarea
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                placeholder={'## Alt başlık\n\nGiriş paragrafı...\n\n### Detaylı bölüm\n\n- Madde 1\n- Madde 2\n\n**Kalın yazı** *italik*'}
                rows={14}
                className="font-mono text-sm"
                data-testid="blog-form-content"
              />
              <p className="text-xs text-slate-400">{form.content.split(/\s+/).filter(Boolean).length} kelime • ~{Math.max(1, Math.round(form.content.split(/\s+/).filter(Boolean).length / 200))} dk okuma</p>
            </div>

            {/* Keywords */}
            <div className="space-y-2">
              <Label>Anahtar Kelimeler <span className="text-xs text-slate-500 font-normal">(SEO için 3-7 adet)</span></Label>
              <div className="flex gap-2">
                <Input
                  value={keywordInput}
                  onChange={(e) => setKeywordInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addKeyword(); } }}
                  placeholder="şirket içi spor + Enter"
                  data-testid="blog-form-keyword-input"
                />
                <Button type="button" onClick={addKeyword} variant="outline" data-testid="blog-form-keyword-add">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {form.keywords.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {form.keywords.map(k => (
                    <span key={k} className="bg-blue-50 text-blue-700 text-xs px-2.5 py-1 rounded-full inline-flex items-center gap-1.5 border border-blue-200">
                      {k}
                      <button type="button" onClick={() => removeKeyword(k)} className="hover:text-red-600" data-testid={`blog-keyword-remove-${k}`}>
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Published Toggle */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200">
              <div>
                <Label className="text-sm font-semibold">Yayın Durumu</Label>
                <p className="text-xs text-slate-500 mt-0.5">{form.published ? 'Yazı /blog sayfasında herkesçe görüntülenir' : 'Taslak — sadece adminler görür'}</p>
              </div>
              <Switch
                checked={form.published}
                onCheckedChange={(v) => setForm({ ...form, published: v })}
                data-testid="blog-form-published"
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
              <Button variant="outline" onClick={() => setShowForm(false)} disabled={saving} data-testid="blog-form-cancel">
                İptal
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-[#0052A5] hover:bg-[#003D7A] text-white"
                data-testid="blog-form-save"
              >
                {saving ? 'Kaydediliyor...' : editingSlug ? 'Güncelle' : 'Yayınla'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BlogTab;
